import {
  Gear,
  Gift,
  Lightbulb,
  Question,
  SignOut,
  User,
} from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { useIntercom } from "react-use-intercom";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { getUserback } from "common/helpers/userbackClient";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import LogOutModal from "common/layouts/WorkspaceLayout/LogOutModal";
import NavItem from "common/layouts/WorkspaceLayout/NavItem";

import TooltipNavItem from "./TooltipNavItem";

import IntercomIcon from "assets/icons/intercom.svg?react";

type ExtraNavItem = {
  label: string;
  icon: ReactNode;
  extraProps?: object;
} & ({ href: string } | { onClick: () => void });

export default function ExtraNavItems({ isExpanded }: { isExpanded: boolean }) {
  const isDesktop = useTwBreakpoint("lg");
  const { show } = useIntercom();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { whitelabelCustomization } = getWhitelabelCustomization();
  const [isLogOutOpen, setIsLogOutOpen] = useState(false);

  const extraNavItems: ExtraNavItem[] = [];

  if (whitelabelCustomization) {
    if (!isDesktop) {
      extraNavItems.push({
        icon: <Gear weight="fill" />,
        label: "Settings",
        href: `/workspaces/${workspaceId}/settings`,
      });
    }
  } else {
    if (!isDesktop) {
      extraNavItems.push({
        icon: <User weight="fill" />,
        label: "Profile settings",
        href: `/workspaces/${workspaceId}/settings`,
      });
      if (window.Cello) {
        extraNavItems.push({
          icon: <Gift weight="fill" />,
          label: "Referral program",
          onClick: () => {
            window.Cello("open");
          },
          extraProps: { "data-target": "cello-button", className: "relative" },
        });
      }
      extraNavItems.push({
        icon: <Lightbulb weight="fill" />,
        label: "Feedback & Roadmap",
        onClick: () => getUserback().openForm(),
      });
      extraNavItems.push({
        icon: <Question weight="fill" />,
        label: "Knowledge base",
        href: "https://help.aimfox.com/en/",
      });
    }
    extraNavItems.push({
      icon: <IntercomIcon />,
      label: "Support live chat",
      onClick: show,
    });
  }
  if (!isDesktop) {
    extraNavItems.push({
      icon: <SignOut />,
      label: "Log out",
      onClick: () => {
        setIsLogOutOpen(true);
      },
    });
  }

  return (
    <>
      {extraNavItems.map((item) => {
        const { label, icon, extraProps } = item;

        const navItem = (
          <NavItem
            key={`${label}_extra_nav_item`}
            isExpanded={isExpanded}
            icon={icon}
          >
            {label}
          </NavItem>
        );

        const extraNavItemComponent =
          "href" in item ? (
            <Link to={item.href} key={label} target="_blank" {...extraProps}>
              {navItem}
            </Link>
          ) : (
            <button
              key={label}
              type="button"
              onClick={item.onClick}
              {...extraProps}
            >
              {navItem}
            </button>
          );

        return (
          <TooltipNavItem
            component={extraNavItemComponent}
            isExpanded={isExpanded}
            label={label}
            key={label}
          />
        );
      })}
      <LogOutModal isOpen={isLogOutOpen} setIsOpen={setIsLogOutOpen} />
    </>
  );
}
